import {ChangeDetectionStrategy, Component, ElementRef, Inject, Injectable, signal, viewChild} from "@angular/core";
import {catchError, delay, map, tap} from "rxjs/operators";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Temporal} from "temporal-polyfill";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {AgendaelementId, ArkivFilnodeModell, DokumentId, DokumentRessursClient, GruppeId, MøteId, MøteModellEnkelMedProtokollinfo, ProtokolldokumentISakModell, SaksdokumentModell, SpørreundersøkelseEnkelMedBehandlingerModell, SpørreundersøkelseRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {MøteDatatjeneste} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {Mimeinfo, mimetyper, mimetyperSomFilInputAccept, tillatteMimetyper} from "felles-ng2/støttefunksjoner/mimetyper";
import {combineLatestWith, filter, forkJoin, from, Observable, of, startWith, switchMap, take} from "rxjs";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";
import {lesZonedDateTimeSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {DokumentKortvisningKomponent} from "../../../dokument/komponenter/dokument-kortvisning/dokument-kortvisning";
import {GoogleDriveFilvelgerKomponent} from "web-ng2/delt/komponenter/filvelger-skytjenester/komponenter/googledrive-filvelger/googledrive-filvelger";
import {MatTab, MatTabGroup} from "@angular/material/tabs";
import {OneDriveFilvelgerKomponent} from "web-ng2/delt/komponenter/filvelger-skytjenester/komponenter/onedrive-filvelger/onedrive-filvelger";
import {TranslateModule} from "@ngx-translate/core";
import {DokumentBakgrunnDirektiv, DokumentFilnavnDirektiv, DokumentHandlingDirektiv, DokumentKomponent} from "web-ng2/delt/komponenter/dokument/dokument";
import {MatTooltip} from "@angular/material/tooltip";
import {MatIcon} from "@angular/material/icon";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {MatButton, MatIconButton} from "@angular/material/button";
import {mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {VelgMøteprotokollKomponent} from "../../../møteprotokoll/komponenter/velg-møteprotokoll/velg-møteprotokoll";
import {lesMøtedato2} from "felles-ng2/modell/møteModellHjelpere";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {asynkArbeidPågårSignal, AsynkArbeidPågårSignalSubArbeid} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {erProtokolldokumentISak} from "felles-ng2/modell/dokumentModellHjelpere";
import {erTomListe} from "felles-ng2/støttefunksjoner/lister";
import {VelgSpørreundersøkelseKomponent} from "../../../spørreundersøkelse/komponenter/velg-spørreundersøkelse/velg-spørreundersøkelse";
import {LasterDataKomponent} from "web-ng2/delt/komponenter/laster-data/laster-data";
import {i18n, I18nTekst, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {DokumentFormatIkonKomponent} from "../../../dokument/komponenter/dokument-format-ikon/dokument-format-ikon";
import {DraOgSlippFilDirektiv} from "web-ng2/delt/direktiver/draOgSlippFil/draOgSlippFil";
import {ArkivVelgNodeKomponent} from "../../../arkiv/komponenter/arkiv-velg-node/arkiv-velg-node";
import {FormControl, ReactiveFormsModule, Validators} from "@angular/forms";
import {scrollTilBunn} from "web-ng2/delt/dom/scrollIntoView";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatToolbar} from "@angular/material/toolbar";
import {DelegerteDokumentvisningOperasjoner, DelegerteDokumentvisningOperasjoner_DI_TOKEN} from "web-ng2/moduler/dokument/komponenter/dokumentvisning/DokumentvisningBasisKomponent";
import {DelegerteDokumentMenyOperasjoner, DelegerteDokumentMenyOperasjoner_DI_TOKEN, delegerteDokumentMenyOperasjonerMedAllFunksjonalitetDeaktivert} from "web-ng2/moduler/dokument/komponenter/dokument-meny/dokument-meny";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {DraOgSlippFilAktivertMeldingKomponent} from "web-ng2/delt/direktiver/draOgSlippFil/draOgSlippFilAktivertMelding";
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import ZonedDateTime = Temporal.ZonedDateTime;

const debug = initDebugger('RegistrerSaksdokumentDialogKomponent');

/**
 * Dialog for registrering av saksdokument.
 * Merk at kaller er ansvarlig for å håndtere opplasting av eventuelle filer bruker har valgt fra disk, se {@link RegistrerSaksdokumentDialogRespons}.
 * Registrering av generet dokument, arkivdokument eller dokument fra skyen (onedrive/google drive) håndteres av komponenten selv.
 */
@Component({
    selector: 'registrer-saksdokument-dialog',
    templateUrl: 'registrer-saksdokument-dialog.html',
    styleUrl: 'registrer-saksdokument-dialog.scss',
    host: {'[class.dra-og-slipp-aktivert]': 'draOgSlippAktivert()'},
    imports: [
        DokumentKortvisningKomponent,
        GoogleDriveFilvelgerKomponent,
        MatTab,
        MatTabGroup,
        OneDriveFilvelgerKomponent,
        TranslateModule,
        DokumentBakgrunnDirektiv,
        DokumentFilnavnDirektiv,
        DokumentHandlingDirektiv,
        DokumentKomponent,
        MatTooltip,
        MatIcon,
        TipsKomponent,
        MatButton,
        VelgMøteprotokollKomponent,
        VelgSpørreundersøkelseKomponent,
        LasterDataKomponent,
        DokumentFormatIkonKomponent,
        MatDialogContent,
        MatDialogActions,
        DraOgSlippFilDirektiv,
        ArkivVelgNodeKomponent,
        InlineMeldingKomponent,
        MatSlideToggle,
        ReactiveFormsModule,
        MatToolbar,
        MatIconButton,
        DraOgSlippFilAktivertMeldingKomponent
    ],
    providers: [
        {provide: DelegerteDokumentMenyOperasjoner_DI_TOKEN, useFactory: (komponent: RegistrerSaksdokumentDialogKomponent) => komponent.delegerteDokumentMenyOperasjoner(), deps: [RegistrerSaksdokumentDialogKomponent]},
        {provide: DelegerteDokumentvisningOperasjoner_DI_TOKEN, useFactory: (komponent: RegistrerSaksdokumentDialogKomponent) => komponent.delegerteDokumentvisningOperasjoner(), deps: [RegistrerSaksdokumentDialogKomponent]}
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegistrerSaksdokumentDialogKomponent {

    static vis(dialog: MatDialog, sak: AgendaelementModellDLM, aktivFane = RegistrerDokumentArkfane.genererte) {
        return dialog.open<RegistrerSaksdokumentDialogKomponent, DialogParametre, RegistrerSaksdokumentDialogRespons>(RegistrerSaksdokumentDialogKomponent,
          {
              data: {sak, aktivFane} as DialogParametre,
              disableClose: true,
              ariaLabelledBy: 'registrer-saksdokument-dialog-tittel',
              minHeight: 500,
              width: '700px'
          });
    }

    // Data fra DialogParametre
    sak: AgendaelementModellDLM;

    arbeider = asynkArbeidPågårSignal();
    aktivArkfane = signal(0);
    draOgSlippAktivert = signal(false);

    //tilgjengelige genererte dokumenter
    genererteDokumenter: { vedKlikk: () => void; tittel: string; filnavn: string; mimeinfo: Mimeinfo; testid: string; }[] = [
        {
            vedKlikk: () => this.registrerAgendadokument(),
            tittel: this.oversettelser.oversett(i18n('Agenda for dette møtet')),
            filnavn: this.oversettelser.oversett(i18n('agenda.pdf')),
            mimeinfo: mimetyper.pdf,
            testid: 'agenda-rapport'
        },
        {
            vedKlikk: () => this.visVelgProtokoll(),
            tittel: this.oversettelser.oversett(i18n('Protokoll fra tidligere møter')),
            filnavn: this.oversettelser.oversett(i18n('protokoll.pdf')),
            mimeinfo: mimetyper.pdf,
            testid: 'protokoll-rapport'
        },
        {
            vedKlikk: () => this.registrerForfalteOppgaverdokument(),
            tittel: this.oversettelser.oversett(i18n('Oppfølging av oppgaver')),
            filnavn: this.oversettelser.oversett(i18n('oppgaver.pdf')),
            mimeinfo: mimetyper.pdf,
            testid: 'oppgaver-rapport'
        },
        {
            vedKlikk: () => this.visVelgSpoerreundersoekelse(),
            tittel: this.oversettelser.oversett(i18n('Rapport fra spørreundersøkelse')),
            filnavn: this.oversettelser.oversett(i18n('spørreundersøkelse resultater.pdf')),
            mimeinfo: mimetyper.pdf,
            testid: 'spørreundersøkelse-rapport'
        }
    ];

    // felter knyttet til gui for 'Protokoll fra tidligere møter'
    viserProtokollvelger = signal(false);
    /** Dersom sann inkluderes også møter der protokoll allerede er til behandling i en sak i {@link #tilgjengeligeProtokoller} */
    inkluderProtokollerTilBehandling = new FormControl(false);
    /** Møter med protokollinfo tilgjengelig for valg dersom bruker 'Protokoll fra tidligere møter' */
    tilgjengeligeProtokoller = toSignal(this.tilgjengeligeProtokollerSomObserverbar(), {initialValue: []});
    valgtProtokollKontroll = VelgMøteprotokollKomponent.opprettSkjema();

    // felter knyttet til gui for 'Rapport fra spørreundersøkelse'
    viserSpoerreundersoekelsevelger = signal(false);
    /** Dersom sann inkluderes også spørreundersøkelser som allerede er til behandling i en sak i {@link #tilgjengeligeSpoerreundersoekelser} */
    inkluderSpoerreundersoekelserTilBehandling = new FormControl(false);
    /** Spørreundersøkelser med behandlingsinfo tilgjengelig for valg dersom bruker 'Rapport fra spørreundersøkelse' */
    tilgjengeligeSpoerreundersoekelser = toSignal(this.tilgjengeligeSpørreundersøkelserSomObserverbar(), {initialValue: []});
    valgtSpoerreundersoekelseKontroll = VelgSpørreundersøkelseKomponent.opprettSkjema();

    // felter knyttet til gui for 'Biblioteksdokument'
    kunArkivdokumenter = ArkivVelgNodeKomponent.kunDokumenter;
    valgtArkivdokumentKontroll = new FormControl<ArkivFilnodeModell | null>(null, {validators: Validators.required});
    private registrerArkivdokumentKnapp = viewChild('registrerArkivdokumentKnapp', {read: ElementRef});

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<RegistrerSaksdokumentDialogKomponent, RegistrerSaksdokumentDialogRespons>,
                private avhengigheter: RegistrerSaksdokumentDialogKomponentAvhengigheter,
                private oversettelser: Oversettelser,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private navigasjon: Navigasjon) {
        this.sak = parametre.sak;
        this.aktivArkfane.set(parametre.aktivFane);

        //skroll til registrer-knapp når arkivdokument er valgt (normalt vil knappen være utenfor vist område)
        this.valgtArkivdokumentKontroll.valueChanges.pipe(
          delay(100), //vent til gui er oppdatert
          map(() => this.registrerArkivdokumentKnapp()!.nativeElement),
          takeUntilDestroyed())
          .subscribe(registrerKnapp => {
              registrerKnapp.focus({preventScroll: true});
              scrollTilBunn(registrerKnapp);
          })
    }

    //opplasting via dra-og-slipp

    filerValgtForOpplasting(filer: File[]) {
        this.håndterFilerValgtForOpplasting(filer);
    }

    //opplasting via filvelger

    tillatteFiltyper(): string {
        return mimetyperSomFilInputAccept(tillatteMimetyper());
    }

    async registrerSaksdokument(event: Event) {
        const target = event.target as HTMLInputElement;
        this.håndterFilerValgtForOpplasting(Array.from(target.files || []));
    }

    //registrering av standard rapporter

    async registrerAgendadokument() {
        await this.avhengigheter.registrerAgendadokument(this.sak.id);
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Agenda har blitt lagt til som saksdokument')));
        this.ferdig()
    }

    async registrerForfalteOppgaverdokument() {
        await this.avhengigheter.registrerForfalteOppgaverdokument(this.sak.id);
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Oppgaver-rapport har blitt lagt til som saksdokument')));
        this.ferdig();
    }


    // handlinger knyttet til gui for 'Protokoll fra tidligere møter'

    visVelgProtokoll() {
        this.viserProtokollvelger.set(true);
    }

    async registrerProtokolldokumentForMoete() {
        await this.arbeider.utfør(async () => {
            await this.avhengigheter.registrerProtokolldokumentForMoete(this.sak.id, this.valgtProtokollKontroll.value!);
        });
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('{antall} protokoller har blitt lagt til som saksdokumenter'), {antall: this.valgtProtokollKontroll.value!.length}));
        this.ferdig();
    }

    avbrytRegistrerProtokolldokumentForMoete() {
        this.viserProtokollvelger.set(false);
    }


    // handlinger knyttet til gui for 'Rapport far spørreundersøkelse'

    visVelgSpoerreundersoekelse() {
        this.viserSpoerreundersoekelsevelger.set(true);
    }

    navigerTilStyreevaluering() {
        this.avbryt();
        this.navigasjon.gåTilGruppeStatistikk(this.sak.eiendeGruppeId);
    }

    async registrerSpoerreundersoekelseRapport() {
        await this.arbeider.utfør(async () => {
            await this.avhengigheter.registrerSpørreundersøkelserapporter(this.sak.id, this.valgtSpoerreundersoekelseKontroll.value!);
        });
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Resultatrapport for spørreundersøkelse har blitt lagt til som saksdokument')));
        this.ferdig();
    }

    avbrytVelgSpoerreundersoekelse() {
        this.viserSpoerreundersoekelsevelger.set(false);
    }

    // registrering av Arkiv-dokument

    delegerteDokumentMenyOperasjoner(): DelegerteDokumentMenyOperasjoner {
        return delegerteDokumentMenyOperasjonerMedAllFunksjonalitetDeaktivert();
    }

    delegerteDokumentvisningOperasjoner(): DelegerteDokumentvisningOperasjoner {
        return {
            //visSkjermingDeaktivert er satt til true på <dokument-kortvisning>, trenger derfor ikke å implementere denne
            visSkjerming: dokumentversjon => {
            },
            //signeringDeaktivert er satt til true på <dokument-kortvisning>, trenger derfor ikke å implementere denne
            visSignatarer: dokumentversjon => {
            }
        };
    }

    async registrerArkivdokument() {
        await this.arbeider.utfør(async () => {
            await this.avhengigheter.registrerArkivdokument(this.sak.id, this.valgtArkivdokumentKontroll.value!);
        });
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Biblioteksdokument blitt lagt til som saksdokument')));
        this.ferdig();
    }

    //registrering av skydokumenter

    skydokumentFilvelgerAktivert() {
        this.ferdig();
    }

    //øvrige offentlige metoder

    avbryt() {
        this.dialogRef.close({avbrutt: true, filerTilOpplasting: []});
    }

    ferdig(filerTilOpplasting: File[] = []) {
        this.dialogRef.close({avbrutt: false, filerTilOpplasting});
    }

    // private hjelpemetoder

    private håndterFilerValgtForOpplasting(filer: File[]) {
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('{antall} filer har blitt lagt til som saksdokumenter'), {antall: filer.length}));
        this.ferdig(filer);
    }

    /** laster tilgjengelige møter for valg når viserProtokollvelger==true */
    private tilgjengeligeProtokollerSomObserverbar(): Observable<MøteModellEnkelMedProtokollinfo[]> {
        let laster: AsynkArbeidPågårSignalSubArbeid | null = null;
        return toObservable(this.viserProtokollvelger).pipe(
          filter(viser => viser),
          //last data kun første gang viserProtokollvelger blir true
          take(1),
          tap(() => laster = this.arbeider.start()),
          switchMap(() => this.avhengigheter.finnMøterOgProtokollbehandlingerForMøteAvholdtFør(this.sak.eiendeGruppeId, lesMøtedato2(this.sak.moete).start)),
          //filter bort dette møte
          map(møter => møter.filter(møteinfo => møteinfo.moete.id != this.sak.moete.id)),
          //filtrer bort møter der protokoll allerede er til behandling, med mindre inkluderProtokollerTilBehandling==true
          combineLatestWith(this.inkluderProtokollerTilBehandling.valueChanges.pipe(startWith(this.inkluderProtokollerTilBehandling.value))),
          map(([møter, inkluderAlleredeTilBehandling]) => inkluderAlleredeTilBehandling ? møter : møter.filter(møte => erTomListe(møte.protokollBehandlesI))),
          //kombiner med et listen over møter som har protokoll som allerede er til behandling i denne saken
          switchMap(møter => this.finnProtokollerTilBehandlingIDenneSaken().pipe(
            //filtrer bort møter med protokoll som allerede er til behandling i denne saken
            map(møterMedProtokollSomBehandlesIDenneSaken => møter.filter(møte => !møterMedProtokollSomBehandlesIDenneSaken.has(møte.moete.id))))),
          tap({next: () => laster?.avsluttet(), error: () => laster!?.avsluttet()})
        );
    }

    private finnProtokollerTilBehandlingIDenneSaken(): Observable<Set<MøteId>> {
        //finn alle saksdokumenter
        return this.avhengigheter.saksdokumenter(this.sak.id).pipe(
          //vi er kun interessert i protokoll-dokumenter
          map(dokumenter => dokumenter.filter(erProtokolldokumentISak)),
          //hent detaljer for alle protokoll-dokumentene fra server
          switchMap(protokolldokumenterISak =>
            //siden forkJoin terminerer dersom den kalles med en tom liste sørger vi for å returnerer [] i slike tilfeller
            erTomListe(protokolldokumenterISak) ? of([]) : forkJoin(protokolldokumenterISak.map(d =>
              from(this.avhengigheter.protokolldokumentdetaljerForProtokolldokument(d.dokumentId as DokumentId))
            ))),
          catchError(e => {
              this.meldingTilBruker.feilmelding(new I18nTekst(i18n('Kunne ikke hente protokolldetaljer')));
              return of([]);
          }),
          //lag en liste med møte-ider basert på listen med protokoller
          map(protokolldokument => new Set(protokolldokument.map(d => d.protokollForMoete.id)))
        );
    }


    /** laster tilgjengelige spørreundersøkelser for valg når viserSpoerreundersoekelsevelger==true */
    private tilgjengeligeSpørreundersøkelserSomObserverbar(): Observable<SpørreundersøkelseEnkelMedBehandlingerModell[]> {
        let laster: AsynkArbeidPågårSignalSubArbeid | null = null;
        return toObservable(this.viserSpoerreundersoekelsevelger).pipe(
          filter(viser => viser),
          //last data kun første gang viserSpoerreundersoekelsevelger blir true
          take(1),
          tap(() => laster = this.arbeider.start()),
          switchMap(() => this.avhengigheter.finnSpørreundersøkelser(this.sak.eiendeGruppeId)),
          //filtrer bort undersøkelser som allerede er til behandling, med mindre inkluderSpoerreundersoekelserTilBehandling==true
          combineLatestWith(this.inkluderSpoerreundersoekelserTilBehandling.valueChanges.pipe(startWith(this.inkluderSpoerreundersoekelserTilBehandling.value))),
          map(([undersøkelser, inkluderAlleredeTilBehandling]) => inkluderAlleredeTilBehandling ? undersøkelser : undersøkelser.filter(undersøkelse => erTomListe(undersøkelse.behandlinger))),
          tap({next: () => laster!.avsluttet(), error: () => laster!.avsluttet()})
        );
    }

}

export type RegistrerSaksdokumentDialogRespons = {
    /** Sann dersom bruker avbrøt dialogen */
    avbrutt: boolean;
    /** Liste med filer bruker har valgt for opplasting, tom liste dersom bruker valgte å registrere et av de generet dokumentene, arkivdokument eller dokument fra skyen (onedrive/google drive) */
    filerTilOpplasting: File[];
};

export type DialogParametre = { sak: AgendaelementModellDLM; aktivFane: RegistrerDokumentArkfane; };

export enum RegistrerDokumentArkfane {
    genererte = 0, bibliotek = 1, skytjenester = 2
}

@Injectable()
export class RegistrerSaksdokumentDialogKomponentAvhengigheter {

    constructor(private saksdokumentDatatjeneste: SaksdokumentDatatjeneste,
                private møteDatatjeneste: MøteDatatjeneste,
                private dokumentRessurs: DokumentRessursClient,
                private spørreundersøkelseRessurs: SpørreundersøkelseRessursClient,
                private meldingTilBruker: MeldingTilBrukerNg2) {
    }

    saksdokumenter(id: AgendaelementId): Observable<SaksdokumentModell[]> {
        return this.saksdokumentDatatjeneste.finnTilknyttetSakFraLokallager(id as AgendaelementId);
    }

    /** Returnerer informasjon om protokoll for møter med startdato lik eller tidligere enn angitt dato. Seneste møte først.  */
    finnMøterOgProtokollbehandlingerForMøteAvholdtFør(gruppeId: GruppeId, dato: ZonedDateTime): Observable<MøteModellEnkelMedProtokollinfo[]> {
        const periode = new ZonedDateTimePeriodeLukket(
          lesZonedDateTimeSerialisert('1970-01-01T00:00:00Z'),
          dato
        );
        return this.møteDatatjeneste.finnMøterOgProtokollbehandlinger(gruppeId, periode).pipe(
          mapTilData(),
          map(møter => møter.reverse()) //finnMøterOgProtokollbehandlinger leverer eldste møte først, vi må returnerer seneste (nyeste) først
        );
    }

    /** Laster {@link ProtokolldokumentISakModell} representasjon av angitt dokumentId, kaller er ansvarlig for å håndtere feil */
    protokolldokumentdetaljerForProtokolldokument(dokumentId: DokumentId): Promise<ProtokolldokumentISakModell> {
        return this.dokumentRessurs.hentProtokolldokumentdetaljer(dokumentId).somPromise()
    }

    /** Laster spørreundersøkelser knyttet til angitt gruppe, ved feil vises feilmelding og tom liste returneres */
    finnSpørreundersøkelser(gruppeId: GruppeId): Observable<SpørreundersøkelseEnkelMedBehandlingerModell[]> {
        return this.spørreundersøkelseRessurs.finnTilknyttetGruppeMedBehandlinger({id: gruppeId})
          .somObservable()
          .pipe(catchError(e => {
              this.meldingTilBruker.feilmelding(new I18nTekst(i18n('Kunne ikke hente spørreundersøkelser')));
              return of([]);
          }));
    }

    async registrerAgendadokument(id: AgendaelementId): Promise<void> {
        return this.saksdokumentDatatjeneste.registrerAgendadokument(id as AgendaelementId);
    }

    async registrerForfalteOppgaverdokument(id: AgendaelementId): Promise<void> {
        return this.saksdokumentDatatjeneste.registrerForfalteOppgaverdokument(id);
    }

    async registrerProtokolldokumentForMoete(id: AgendaelementId, møter: MøteModellEnkelMedProtokollinfo[]): Promise<void> {
        return this.saksdokumentDatatjeneste.registrerProtokolldokumenterForMoete(id, møter.map(møteinfo => møteinfo.moete.id))
    }

    async registrerArkivdokument(id: AgendaelementId, arkivfil: ArkivFilnodeModell): Promise<void> {
        return this.saksdokumentDatatjeneste.registrerArkivdokument(id, {
            dokumentVersjonId: arkivfil.dokument.id as DokumentId,
            arkivFilnodeId: arkivfil.id
        })
    }

    async registrerSpørreundersøkelserapporter(id: AgendaelementId, undersøkelser: SpørreundersøkelseEnkelMedBehandlingerModell[]): Promise<void> {
        return this.saksdokumentDatatjeneste.registrerSpørreundersøkelserapporter(id, undersøkelser.map(undersøkelse => undersøkelse.id));
    }
}
